import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';

const getOrdinalSuffix = (day) => {
  if (day > 3 && day < 21) return 'th';
  switch (day % 10) {
    case 1:  return "st";
    case 2:  return "nd";
    case 3:  return "rd";
    default: return "th";
  }
};

const formatToStandardDate = (date) => {
  if (!date || isNaN(date.getTime())) return "";
  const day = date.getDate();
  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  return `${day}${getOrdinalSuffix(day)} ${month} ${year}`;
};

const parseStandardDate = (dateStr) => {
  if (!dateStr || typeof dateStr !== 'string') return null;
  let cleanDate = dateStr.trim();
  // Strip weekday prefixes if present
  cleanDate = cleanDate.replace(/^(mon|tue|wed|thu|fri|sat|sun)[a-z]*\s+/i, "");
  if (!cleanDate) return null;

  // 1. Check YYYY-MM-DD
  const isoRegex = /^(\d{4})-(\d{2})-(\d{2})/;
  const isoMatch = cleanDate.match(isoRegex);
  if (isoMatch) {
    const y = parseInt(isoMatch[1], 10);
    const m = parseInt(isoMatch[2], 10) - 1;
    const d = parseInt(isoMatch[3], 10);
    return new Date(y, m, d);
  }

  // 2. Check DD-MM-YYYY
  const dmyRegex = /^(\d{1,2})-(\d{1,2})-(\d{4})$/;
  const dmyMatch = cleanDate.match(dmyRegex);
  if (dmyMatch) {
    const d = parseInt(dmyMatch[1], 10);
    const m = parseInt(dmyMatch[2], 10) - 1;
    const y = parseInt(dmyMatch[3], 10);
    return new Date(y, m, d);
  }

  // 3. Check DD-MMM-YYYY or DD MMM YYYY or D MMMM YYYY
  const parts = cleanDate.split(/[\s\-\,]+/);
  if (parts.length >= 2) {
    const day = parseInt(parts[0].replace(/\D/g, ""), 10);
    const monthStr = parts[1].toLowerCase().substring(0, 3);
    const months = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
    const monthIndex = months.indexOf(monthStr);

    if (monthIndex !== -1 && !isNaN(day)) {
      let year = 2026;
      if (parts[2] && /^\d{4}$/.test(parts[2])) {
        year = parseInt(parts[2], 10);
      }
      return new Date(year, monthIndex, day);
    }
  }

  const parsed = new Date(cleanDate);
  if (parsed && !isNaN(parsed.getTime())) {
    return parsed;
  }
  return null;
};

export { formatToStandardDate, parseStandardDate };

const CustomDatePicker = ({
  value,
  onChange,
  placeholder = "Select date",
  className = "",
  inputClassName = "",
  leftIcon,
  minDate,
  maxDate,
  disabledDate
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);
  const popoverRef = useRef(null);

  const parsedDate = parseStandardDate(value);
  const parsedMinDate = minDate ? (minDate instanceof Date ? minDate : parseStandardDate(minDate)) : null;
  const parsedMaxDate = maxDate ? (maxDate instanceof Date ? maxDate : parseStandardDate(maxDate)) : null;

  const activeDate = parsedDate || parsedMinDate || new Date();

  const [viewMonth, setViewMonth] = useState(activeDate.getMonth());
  const [viewYear, setViewYear] = useState(activeDate.getFullYear());

  useEffect(() => {
    if (parsedDate) {
      setViewMonth(parsedDate.getMonth());
      setViewYear(parsedDate.getFullYear());
    } else if (parsedMinDate) {
      setViewMonth(parsedMinDate.getMonth());
      setViewYear(parsedMinDate.getFullYear());
    }
  }, [value, minDate]);

  const [popoverStyle, setPopoverStyle] = useState({
    left: 0,
    top: 0
  });

  const updatePosition = useCallback(() => {
    if (wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      const popoverWidth = Math.min(276, window.innerWidth - 24);
      const popoverHeight = popoverRef.current?.offsetHeight || 310;
      const padding = 12;

      // Vertical position (smart dropUp calculation)
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const shouldDropUp = spaceBelow < (popoverHeight + 8) && spaceAbove > spaceBelow;

      let top;
      if (shouldDropUp) {
        top = rect.top - popoverHeight - 8;
      } else {
        top = rect.bottom + 8;
      }

      // Clamp vertical position so it stays within viewport
      if (top < padding) {
        top = padding;
      } else if (top + popoverHeight > window.innerHeight - padding) {
        top = Math.max(padding, window.innerHeight - padding - popoverHeight);
      }

      // Horizontal position: align with left edge of input, clamped to viewport
      let left = rect.left;
      if (left + popoverWidth > window.innerWidth - padding) {
        left = window.innerWidth - padding - popoverWidth;
      }
      if (left < padding) {
        left = padding;
      }

      setPopoverStyle({
        position: 'fixed',
        left: `${left}px`,
        top: `${top}px`,
        zIndex: 999999
      });
    }
  }, []);

  const handleToggle = () => {
    if (!isOpen) {
      updatePosition();
    }
    setIsOpen((prev) => !prev);
  };

  useEffect(() => {
    if (!isOpen) return;

    updatePosition();

    const handleScrollOrResize = (e) => {
      if (popoverRef.current && popoverRef.current.contains(e.target)) return;
      updatePosition();
    };

    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);

    return () => {
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [isOpen, updatePosition]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target) &&
        popoverRef.current &&
        !popoverRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [isOpen]);

  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const handlePrevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear(prev => prev - 1);
    } else {
      setViewMonth(prev => prev - 1);
    }
  };

  const handleNextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear(prev => prev + 1);
    } else {
      setViewMonth(prev => prev + 1);
    }
  };

  const isDayDisabled = (day) => {
    if (!day) return true;
    const currentDayDate = new Date(viewYear, viewMonth, day);
    currentDayDate.setHours(0, 0, 0, 0);

    if (parsedMinDate) {
      const minCopy = new Date(parsedMinDate);
      minCopy.setHours(0, 0, 0, 0);
      if (currentDayDate < minCopy) return true;
    }

    if (parsedMaxDate) {
      const maxCopy = new Date(parsedMaxDate);
      maxCopy.setHours(0, 0, 0, 0);
      if (currentDayDate > maxCopy) return true;
    }

    if (typeof disabledDate === 'function') {
      return disabledDate(currentDayDate);
    }

    return false;
  };

  const handleDateClick = (day) => {
    if (isDayDisabled(day)) return;
    const selectedDate = new Date(viewYear, viewMonth, day);
    onChange(formatToStandardDate(selectedDate));
    setIsOpen(false);
  };

  const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const firstDay = getFirstDayOfMonth(viewYear, viewMonth);

  const days = [];
  for (let i = 0; i < firstDay; i++) {
    days.push(null);
  }
  for (let i = 1; i <= daysInMonth; i++) {
    days.push(i);
  }

  const isSelected = (day) => {
    if (!day || !parsedDate) return false;
    return parsedDate.getDate() === day &&
      parsedDate.getMonth() === viewMonth &&
      parsedDate.getFullYear() === viewYear;
  };

  const isToday = (day) => {
    if (!day) return false;
    const today = new Date();
    return today.getDate() === day &&
      today.getMonth() === viewMonth &&
      today.getFullYear() === viewYear;
  };

  const years = [];
  const startYear = Math.min(new Date().getFullYear(), viewYear - 2);
  const endYear = Math.max(new Date().getFullYear() + 5, viewYear + 4);
  for (let y = startYear; y <= endYear; y++) {
    years.push(y);
  }

  return (
    <div className={`relative min-w-0 ${className}`} ref={wrapperRef}>
      <div 
        onClick={handleToggle}
        className={`flex items-center gap-2.5 w-full cursor-pointer bg-white border border-slate-200 rounded-xl transition-all text-gray-800 select-none overflow-hidden hover:border-slate-300 shadow-sm px-3.5 h-[42px] ${inputClassName}`}
      >
        {leftIcon ? (
          <div className="text-slate-400 pointer-events-none flex items-center justify-center shrink-0">
            {leftIcon}
          </div>
        ) : null}
        <span className={`${value ? "text-gray-800" : "text-gray-400"} truncate flex-1 min-w-0 text-left text-xs font-semibold`}>
          {value ? (formatToStandardDate(parseStandardDate(value)) || value) : placeholder}
        </span>
        {!leftIcon && (
          <CalendarIcon size={15} className="text-slate-400 shrink-0 ml-auto pointer-events-none" />
        )}
      </div>

      {isOpen && typeof document !== 'undefined' && createPortal(
        <div 
          ref={popoverRef}
          style={popoverStyle} 
          onClick={(e) => e.stopPropagation()}
          className="w-[276px] max-w-[calc(100vw-24px)] bg-white border border-slate-200 rounded-2xl shadow-2xl p-3 sm:p-4 animate-in fade-in slide-in-from-top-1 duration-150 select-none"
        >
          <div className="flex items-center justify-between mb-3">
            <button 
              type="button"
              onClick={handlePrevMonth}
              className="p-1.5 hover:bg-slate-100 rounded-full transition-colors text-gray-600 cursor-pointer"
            >
              <ChevronLeft size={16} />
            </button>
            
            <div className="flex items-center gap-1.5">
              <select
                value={viewMonth}
                onChange={(e) => setViewMonth(parseInt(e.target.value))}
                className="bg-transparent border-0 font-semibold text-gray-800 focus:outline-none focus:ring-0 text-xs sm:text-sm py-0 pr-5 pl-1 cursor-pointer"
              >
                {months.map((m, i) => (
                  <option key={i} value={i}>{m}</option>
                ))}
              </select>
              <select
                value={viewYear}
                onChange={(e) => setViewYear(parseInt(e.target.value))}
                className="bg-transparent border-0 font-semibold text-gray-800 focus:outline-none focus:ring-0 text-xs sm:text-sm py-0 pr-5 pl-1 cursor-pointer"
              >
                {years.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>

            <button 
              type="button"
              onClick={handleNextMonth}
              className="p-1.5 hover:bg-slate-100 rounded-full transition-colors text-gray-600 cursor-pointer"
            >
              <ChevronRight size={16} />
            </button>
          </div>

          <div className="grid grid-cols-7 gap-0.5 sm:gap-1 text-center mb-1">
            {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day, idx) => (
              <span key={idx} className="text-[10px] font-bold text-gray-400 uppercase">
                {day}
              </span>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-0.5 sm:gap-1">
            {days.map((day, idx) => {
              if (!day) return <span key={idx} className="w-7 h-7 sm:w-8 sm:h-8" />;
              const disabled = isDayDisabled(day);
              const selected = isSelected(day);
              const today = isToday(day);

              return (
                <div key={idx} className="aspect-square flex items-center justify-center">
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => handleDateClick(day)}
                    className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs font-semibold transition-all cursor-pointer ${
                      disabled
                        ? "opacity-25 cursor-not-allowed text-gray-400 select-none pointer-events-none"
                        : selected 
                          ? "bg-teal-500 text-white shadow-sm" 
                          : today
                            ? "border border-teal-500 text-teal-600 bg-teal-50/50 hover:bg-teal-100/50"
                            : "text-gray-700 hover:bg-slate-100"
                    }`}
                  >
                    {day}
                  </button>
                </div>
              );
            })}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default CustomDatePicker;
